import React, { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { FetchMoreQueryOptions } from '@apollo/client';
import TagPage from '@fuww/library/src/Layouts/TagPage';
import { GridCell } from '@fuww/library/src/Grid';
import MediumCard from '@fuww/library/src/Cards/Medium';
import { useSiteContext } from '../components/SiteContext';
import withApollo from '../lib/apollo';
import withQuery from '../lib/withQuery';
import messages from '../lib/messages.json';
import getProxiedImages from '../lib/getProxiedImages';
import Head from '../components/Head';
import HomeLink from '../components/HomeLink';
import LinkAndAnchor from '../components/LinkAndAnchor';
import ImageWithUrls from '../components/ImageWithUrls';
import Loader from '../components/Loader';
import Layout from '../components/Layout';
import specialPagesConnection
  from '../lib/queries/specials/specialPagesConnection.graphql';
import { articleListImageConfigurations } from '../lib/imageConfigurations';
import loadMore from '../lib/queries/loadMore';
import LoadMore from '../components/LoadMore';
import {
  SpecialPagesConnectionQueryVariables,
  SpecialPagesConnectionQuery,
  SpecialPageSorting,
}
  from '../lib/graphql/api/graphql';

export interface SpecialPageNode {
  id: number;
  slug: string;
  imageUrls: string[];
  mobileImageUrls: string[];
  title: string;
}

interface SpecialsDirectoryWithQueryProperties {
  data: SpecialPagesConnectionQuery;
  variables: SpecialPagesConnectionQueryVariables;
  loading: boolean;
  fetchMore: (
    variables: FetchMoreQueryOptions<SpecialPagesConnectionQueryVariables>,
  ) => void;
}

const SpecialsDirectoryWithQuery = withQuery(({
  data,
  variables,
  loading,
  fetchMore,
}: SpecialsDirectoryWithQueryProperties) => {
  const { specialPagesConnection: { edges } } = { ...data };

  return (
    <>
      {
        edges.map(({
          node,
        },
        index) => {
          const isFirstImage = index === 0;
          const fetchPriority = isFirstImage ? 'high' : 'low';

          const {
            id,
            slug,
            imageUrls: originalImageUrls,
            mobileImageUrls,
            title,
          } = node;
          const { imageUrls, srcSet } = getProxiedImages(
            variables,
            mobileImageUrls[0] ? mobileImageUrls : originalImageUrls,
          );

          return (
            <GridCell
              sm={6}
              lg={3}
              key={id}
              padding="16px"
            >
              <LinkAndAnchor
                route="specials"
                params={{ slug }}
                useRouter
              >
                <MediumCard
                  image={(
                    <ImageWithUrls
                      // eslint-disable-next-line max-len
                      sizes="(min-width: 1440px) 330px, (min-width: 1024px) 24vw, (min-width: 600px) 48vw, 100vw"
                      imageUrls={imageUrls}
                      alt={title}
                      srcSet={srcSet}
                      finalFetchpriority={fetchPriority}
                      placeholderFetchpriority={fetchPriority}
                    />
                  )}
                  title={title}
                  withPageMargins
                />
              </LinkAndAnchor>
            </GridCell>
          );
        })
      }
      <GridCell padding="16px">
        <LoadMore
          loading={loading}
          hasNextPage={data.specialPagesConnection.pageInfo.hasNextPage}
          fetchMore={
            () => loadMore({
              variables,
            })(data, 'specialPagesConnection', fetchMore)
          }
        />
      </GridCell>
    </>
  );
});

const LIMIT = 12;

const SpecialsDirectoryPage = () => {
  const intl = useIntl();
  const { newsboard } = useSiteContext();

  const breadcrumbs = [
    <HomeLink />,
    <LinkAndAnchor
      route="specials-directory"
      passHref
    >
      {`${intl.formatMessage(
        messages.specialsDirectoryTitle,
      )}`}
    </LinkAndAnchor>,
  ];

  return (
    <>
      <Head
        title={`${intl.formatMessage(
          messages.specialsDirectoryTitle,
        )}`}
        description={`${intl.formatMessage(
          messages.specialsDirectoryMetaDescription,
        )}`}
        subjectType="SpecialsDirectory"
      />
      <TagPage
        breadcrumbs={breadcrumbs}
        title={`${intl.formatMessage(
          messages.specialsDirectoryTitle,
        )}`}
      >
        <SpecialsDirectoryWithQuery
          query={specialPagesConnection}
          variables={{
            first: LIMIT,
            newsboard,
            imageConfigurations: articleListImageConfigurations,
            sort: SpecialPageSorting.RELEVANCE_DESCENDING,
          }}
          errorMessage="Error loading special pages"
          loader={<Loader fullScreen />}
        />
      </TagPage>
    </>
  );
};

SpecialsDirectoryPage.getLayout = (page: ReactNode) => (
  <Layout>{page}</Layout>
);

const SpecialsDirectoryPageWithApollo = withApollo(SpecialsDirectoryPage);

export default SpecialsDirectoryPageWithApollo;
